import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Unlink, RefreshCw, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import {
    WebflowSite,
    WebflowAuthorisedSite,
    WebflowCollection,
    WebflowField,
    startWebflowOAuth,
    listWebflowAuthorisedSites,
    selectWebflowSite,
    selectWebflowCollection,
    saveWebflowFieldMap,
    listWebflowSites,
    disconnectWebflowSite,
} from '../../services/api';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';

const AIME_FIELDS = [
    { key: 'title',          label: 'Title',        required: true },
    { key: 'slug',           label: 'Slug',          required: true },
    { key: 'body_html',      label: 'Body (HTML)',    required: true },
    { key: 'excerpt',        label: 'Excerpt',        required: false },
    { key: 'featured_image', label: 'Featured Image', required: false },
    { key: 'author_name',    label: 'Author Name',    required: false },
];

interface Props {
    projectId: string;
    /** Pass webflow_site_id from URL params when webflow_connected=1 is present */
    pendingWebflowSiteId?: string | null;
}

type WizardStep = 'loading' | 'not_connected' | 'picking_site' | 'picking_collection' | 'mapping_fields' | 'connected' | 'error';

const WebflowConnectCard: React.FC<Props> = ({ projectId, pendingWebflowSiteId }) => {
    const [step, setStep] = useState<WizardStep>('loading');
    const [connectedSite, setConnectedSite] = useState<WebflowSite | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [working, setWorking] = useState(false);

    // Site picker state
    const [pendingSiteId, setPendingSiteId] = useState<string | null>(pendingWebflowSiteId || null);
    const [authorisedSites, setAuthorisedSites] = useState<WebflowAuthorisedSite[]>([]);
    const [selectedExtSiteId, setSelectedExtSiteId] = useState('');
    const [selectedSiteName, setSelectedSiteName] = useState('');

    // Collection picker state
    const [collections, setCollections] = useState<WebflowCollection[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState('');

    // Field mapper state
    const [fields, setFields] = useState<WebflowField[]>([]);
    const [fieldMap, setFieldMap] = useState<Record<string, string>>({});

    // Confirm-before-save modal state (Item 6 — full-site-rebuild warning)
    const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

    const loadConnected = useCallback(async () => {
        try {
            const data = await listWebflowSites(projectId);
            const site = data.sites.find(s => s.is_fully_configured) || data.sites[0] || null;
            if (site) {
                setConnectedSite(site);
                setStep('connected');
            } else {
                setStep('not_connected');
            }
        } catch {
            setStep('not_connected');
        }
    }, [projectId]);

    useEffect(() => {
        if (pendingSiteId) {
            setStep('picking_site');
            listWebflowAuthorisedSites(pendingSiteId)
                .then(d => setAuthorisedSites(d.sites))
                .catch((e: any) => { setError(e.message || 'Failed to load sites'); setStep('error'); });
        } else {
            loadConnected();
        }
    }, [pendingSiteId, loadConnected]);

    const handleConnect = async () => {
        setWorking(true);
        try {
            const { auth_url } = await startWebflowOAuth(projectId);
            window.location.href = auth_url;
        } catch (e: any) {
            setError(e.message || 'Failed to start Webflow OAuth');
            setStep('error');
            setWorking(false);
        }
    };

    const handleSelectSite = async () => {
        if (!selectedExtSiteId || !pendingSiteId) return;
        setWorking(true);
        try {
            const data = await selectWebflowSite({
                webflow_site_id: pendingSiteId,
                webflow_site_external_id: selectedExtSiteId,
                site_display_name: selectedSiteName,
            });
            setCollections(data.collections);
            setStep('picking_collection');
        } catch (e: any) {
            setError(e.message || 'Failed to select site');
            setStep('error');
        } finally { setWorking(false); }
    };

    const handleSelectCollection = async () => {
        if (!selectedCollectionId || !pendingSiteId) return;
        setWorking(true);
        try {
            const data = await selectWebflowCollection({
                webflow_site_id: pendingSiteId,
                blog_collection_id: selectedCollectionId,
            });
            setFields(data.fields);
            const initial: Record<string, string> = {};
            AIME_FIELDS.forEach(f => {
                const detected = data.auto_detected_field_map[f.key];
                if (detected) initial[f.key] = detected;
            });
            setFieldMap(initial);
            setStep('mapping_fields');
        } catch (e: any) {
            setError(e.message || 'Failed to load collection fields');
            setStep('error');
        } finally { setWorking(false); }
    };

    // Gate the save behind the full-site-rebuild confirm modal (Item 6).
    // Click flow: "Save Field Mapping" → validates → opens modal → user confirms → commitSaveFieldMap.
    const handleSaveFieldMap = () => {
        if (!pendingSiteId) return;
        const missing = AIME_FIELDS.filter(f => f.required && !fieldMap[f.key]);
        if (missing.length > 0) {
            setError(`Required field mappings missing: ${missing.map(f => f.label).join(', ')}`);
            return;
        }
        setError(null);
        setConfirmSaveOpen(true);
    };

    const commitSaveFieldMap = async () => {
        if (!pendingSiteId) return;
        setConfirmSaveOpen(false);
        setWorking(true);
        try {
            await saveWebflowFieldMap({ webflow_site_id: pendingSiteId, field_map: fieldMap });
            setPendingSiteId(null);
            // Strip OAuth callback params so a page refresh doesn't re-enter the wizard
            window.history.replaceState({}, '', window.location.pathname);
            await loadConnected();
        } catch (e: any) {
            setError(e.message || 'Failed to save field mapping');
        } finally { setWorking(false); }
    };

    // Re-enter the wizard from the connected card so the user can fix a drifted
    // field mapping (Item 12). React-state only — no backend write until they save.
    const handleRemapFields = () => {
        if (!connectedSite) return;
        setError(null);
        setPendingSiteId(connectedSite.webflow_site_id);
        setStep('picking_site');
        listWebflowAuthorisedSites(connectedSite.webflow_site_id)
            .then(d => setAuthorisedSites(d.sites))
            .catch((e: any) => { setError(e.message || 'Failed to load sites'); setStep('error'); });
    };

    // Restart OAuth when credentials have been wiped (Item 14).
    const handleReconnect = async () => {
        setWorking(true);
        try {
            const { auth_url } = await startWebflowOAuth(projectId);
            window.location.href = auth_url;
        } catch (e: any) {
            setError(e.message || 'Failed to start Webflow reconnect');
            setStep('error');
            setWorking(false);
        }
    };

    const handleDisconnect = async () => {
        if (!connectedSite) return;
        // Item 15 — note that AIME's revoke call is best-effort and the user
        // may need to remove access in their Webflow workspace too.
        if (!window.confirm("Disconnect Webflow? Scheduled publications targeting this site will fail, and revoke AIME's access in your Webflow workspace if you want to fully remove it.")) return;
        setWorking(true);
        try {
            await disconnectWebflowSite(connectedSite.webflow_site_id);
            setConnectedSite(null);
            setStep('not_connected');
        } catch (e: any) {
            setError(e.message || 'Failed to disconnect');
        } finally { setWorking(false); }
    };

    const cardStyle: React.CSSProperties = {
        background: 'var(--panel)',
        border: '1px solid var(--stroke)',
        borderRadius: 'var(--radius-md)',
        padding: '20px',
        marginBottom: '16px',
    };

    const labelStyle: React.CSSProperties = {
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--text-secondary)',
        marginBottom: '4px',
        display: 'block',
    };

    const selectStyle: React.CSSProperties = {
        width: '100%',
        padding: '8px 10px',
        background: 'var(--panel-strong)',
        border: '1px solid var(--stroke)',
        borderRadius: 'var(--radius-sm)',
        color: 'var(--text)',
        fontSize: '14px',
        marginBottom: '12px',
    };

    const btnPrimary: React.CSSProperties = {
        background: 'var(--primary)',
        color: '#fff',
        border: 'none',
        borderRadius: '9999px',
        padding: '8px 20px',
        fontWeight: 600,
        cursor: working ? 'not-allowed' : 'pointer',
        opacity: working ? 0.6 : 1,
        fontSize: '14px',
    };

    const btnDanger: React.CSSProperties = {
        background: 'var(--error, #dc2626)',
        color: '#fff',
        border: 'none',
        borderRadius: '9999px',
        padding: '8px 16px',
        fontWeight: 600,
        cursor: working ? 'not-allowed' : 'pointer',
        opacity: working ? 0.6 : 1,
        fontSize: '14px',
    };

    if (step === 'loading') {
        return (
            <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '14px' }}>Loading Webflow connection…</span>
                </div>
            </div>
        );
    }

    return (
        <div style={cardStyle}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <Globe size={20} style={{ color: 'var(--primary)' }} />
                <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text)' }}>Webflow CMS</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Publish AIME-generated blog posts directly to your Webflow site
                    </div>
                </div>
            </div>

            {step === 'error' && (
                <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>Connection error</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>{error}</div>
                    </div>
                </div>
            )}

            {(step === 'not_connected' || step === 'error') && (
                <div>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 14px' }}>
                        Connect your Webflow account to publish content directly to your site's CMS.
                    </p>
                    <button style={btnPrimary} onClick={handleConnect} disabled={working}>
                        {working ? 'Redirecting…' : 'Connect Webflow'}
                    </button>
                </div>
            )}

            {step === 'picking_site' && (
                <div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                        Choose which Webflow site to connect to this project:
                    </div>
                    <label style={labelStyle}>Webflow Site</label>
                    <select
                        style={selectStyle}
                        value={selectedExtSiteId}
                        onChange={e => {
                            const site = authorisedSites.find(s => s.id === e.target.value);
                            setSelectedExtSiteId(e.target.value);
                            setSelectedSiteName(site?.name || '');
                        }}
                    >
                        <option value="">— select a site —</option>
                        {authorisedSites.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <button style={btnPrimary} onClick={handleSelectSite} disabled={working || !selectedExtSiteId}>
                        {working ? 'Loading…' : 'Select Site'}
                    </button>
                </div>
            )}

            {step === 'picking_collection' && (
                <div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                        Which collection is your blog?
                    </div>
                    <label style={labelStyle}>Blog Collection</label>
                    <select style={selectStyle} value={selectedCollectionId} onChange={e => setSelectedCollectionId(e.target.value)}>
                        <option value="">— select a collection —</option>
                        {collections.map(c => (
                            <option key={c.id} value={c.id}>{c.displayName}</option>
                        ))}
                    </select>
                    <button style={btnPrimary} onClick={handleSelectCollection} disabled={working || !selectedCollectionId}>
                        {working ? 'Loading…' : 'Select Collection'}
                    </button>
                </div>
            )}

            {step === 'mapping_fields' && (
                <div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        Map AIME fields to your Webflow collection fields.
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                        Publishing will trigger a full Webflow site rebuild. Any other drafts staged in Webflow Designer will also go live.
                    </div>
                    {error && <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '10px' }}>{error}</div>}
                    {AIME_FIELDS.map(af => (
                        <div key={af.key} style={{ marginBottom: '10px' }}>
                            <label style={labelStyle}>
                                {af.label}{af.required && <span style={{ color: '#dc2626' }}> *</span>}
                            </label>
                            <select
                                style={{ ...selectStyle, marginBottom: 0, borderColor: af.required && !fieldMap[af.key] ? 'rgba(220,38,38,0.5)' : undefined }}
                                value={fieldMap[af.key] || ''}
                                onChange={e => setFieldMap(prev => ({ ...prev, [af.key]: e.target.value }))}
                            >
                                <option value="">— not mapped —</option>
                                {fields.map(f => (
                                    <option key={f.id} value={f.id}>{f.displayName} ({f.type})</option>
                                ))}
                            </select>
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                        <button style={btnPrimary} onClick={handleSaveFieldMap} disabled={working}>
                            {working ? 'Saving…' : 'Save Field Mapping'}
                        </button>
                    </div>
                </div>
            )}

            {step === 'connected' && connectedSite && (
                <div>
                    {/* Incomplete-wizard resume banner */}
                    {!connectedSite.is_fully_configured && (
                        <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.4)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                                Setup not complete — field mapping not saved yet.
                            </span>
                            <button
                                style={{ ...btnPrimary, padding: '6px 14px', fontSize: '13px' }}
                                disabled={working}
                                onClick={() => {
                                    setPendingSiteId(connectedSite.webflow_site_id);
                                    setStep('picking_site');
                                    listWebflowAuthorisedSites(connectedSite.webflow_site_id)
                                        .then(d => setAuthorisedSites(d.sites))
                                        .catch(e => { setError(e.message); setStep('error'); });
                                }}
                            >
                                Resume setup
                            </button>
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <CheckCircle size={16} style={{ color: connectedSite.is_fully_configured ? 'var(--success, #16a34a)' : 'rgba(234,179,8,0.8)' }} />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                            {connectedSite.connected_email || 'Connected'}
                            {connectedSite.site_display_name && ` → ${connectedSite.site_display_name}`}
                            {connectedSite.blog_collection_name && ` → ${connectedSite.blog_collection_name}`}
                        </span>
                    </div>
                    {connectedSite.last_publish_error && (
                        (() => {
                            const isFieldDrift = connectedSite.last_publish_error.startsWith('FIELD_DRIFT:')
                                || connectedSite.last_publish_error.includes('FIELD_DRIFT:');
                            return (
                                <div style={{ marginBottom: '10px' }}>
                                    <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '6px' }}>
                                        {isFieldDrift
                                            ? "A field in your Webflow collection no longer matches your mapping — re-map fields to continue publishing."
                                            : `Last publish error: ${connectedSite.last_publish_error}`}
                                    </div>
                                    {isFieldDrift && (
                                        <button
                                            style={{ ...btnPrimary, padding: '6px 14px', fontSize: '13px' }}
                                            disabled={working}
                                            onClick={handleRemapFields}
                                        >
                                            Re-map fields
                                        </button>
                                    )}
                                </div>
                            );
                        })()
                    )}
                    {connectedSite.last_published_at && (
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                            Last published: {new Date(connectedSite.last_published_at).toLocaleString()}
                        </div>
                    )}
                    {connectedSite.is_credentials_cleared && (
                        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: '14px' }}>
                            <div style={{ fontSize: '13px', color: 'var(--text)', marginBottom: '8px' }}>
                                Webflow access was revoked. Reconnect to resume publishing.
                            </div>
                            <button style={btnPrimary} onClick={handleReconnect} disabled={working}>
                                {working ? 'Redirecting…' : 'Reconnect Webflow'}
                            </button>
                        </div>
                    )}
                    {connectedSite.is_fully_configured && !connectedSite.is_credentials_cleared && (
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '14px', padding: '8px 10px', background: 'var(--panel-strong)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--stroke)' }}>
                            Publishing through AIME triggers a full Webflow site publish — any other drafts you have staged in Webflow Designer will also go live.
                        </div>
                    )}
                    <button style={btnDanger} onClick={handleDisconnect} disabled={working}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Unlink size={14} />
                            {working ? 'Disconnecting…' : 'Disconnect'}
                        </span>
                    </button>
                </div>
            )}

            {/* Item 6 — full-site-rebuild confirmation modal.
                Cancel = React state only, Confirm = backend write via commitSaveFieldMap. */}
            <GlassModal
                isOpen={confirmSaveOpen}
                onClose={() => setConfirmSaveOpen(false)}
                title="Confirm field mapping"
                size="sm"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setConfirmSaveOpen(false)}>
                            Cancel
                        </GlassButton>
                        <GlassButton variant="primary" onClick={commitSaveFieldMap}>
                            Confirm — Save Mapping
                        </GlassButton>
                    </>
                }
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <AlertTriangle size={18} style={{ color: 'var(--danger, #dc2626)', flexShrink: 0, marginTop: 2 }} />
                    <div style={{ fontSize: '14px', color: 'var(--text)', lineHeight: 1.5 }}>
                        <div style={{ marginBottom: '8px' }}>
                            {selectedSiteName && <strong>{selectedSiteName}</strong>}
                            {selectedSiteName && ' → '}
                            {collections.find(c => c.id === selectedCollectionId)?.displayName || 'Selected collection'}
                        </div>
                        <div style={{ color: 'var(--text-secondary)' }}>
                            Publishing through AIME triggers a full Webflow site rebuild.
                            Any other drafts you have staged in Webflow Designer will also go live on save.
                        </div>
                    </div>
                </div>
            </GlassModal>
        </div>
    );
};

export default WebflowConnectCard;
